import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ui } from "../shared/styles/uiSystem"
import { useActiveClinic } from "../context/ClinicContext"
import { useClinicPlanSnapshot } from "../features/subscriptions/hooks/useClinicPlanSnapshot"
import { DEFAULT_USER_ID } from "../config/appConfig"
import { apiFetch } from "../utils/apiFetch"

function getScheduleStorageKey(clinicId) {
  return `odontoflow_schedule_settings_${clinicId || "default"}`
}

function parseStoredConfig(rawValue) {
  try {
    const parsed = JSON.parse(rawValue)
    return parsed && typeof parsed === "object" ? parsed : null
  } catch {
    return null
  }
}

function countEnabledDays(hours) {
  if (!hours || typeof hours !== "object") return 0
  return Object.values(hours).filter((item) => item?.enabled).length
}

function countBlockedDates(blockedDatesText) {
  return String(blockedDatesText || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean).length
}

function buildQueryString(params = {}) {
  const cleanParams = Object.entries(params || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = String(value)
    }

    return acc
  }, {})

  return new URLSearchParams(cleanParams).toString()
}

async function requestFirstSuccess(paths, params) {
  const query = buildQueryString(params)

  for (const path of paths) {
    try {
      const url = query ? `${path}?${query}` : path
      const res = await apiFetch(url)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        continue
      }

      return Array.isArray(data) ? data : data || null
    } catch {
      // probar siguiente ruta
    }
  }

  return null
}

function buildStatusTone(type) {
  if (type === "success") return "success"
  if (type === "warning") return "warning"
  if (type === "danger") return "danger"
  return "info"
}

export default function SettingsHomePage() {
  const navigate = useNavigate()
  const { activeClinicId } = useActiveClinic()

  const clinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : localStorage.getItem("odontoflow_clinic_id") || ""

  const userId = localStorage.getItem("odontoflow_user_id") || DEFAULT_USER_ID

  const { data: planSnapshot } = useClinicPlanSnapshot(clinicId || null)
  const currentPlanName = planSnapshot?.plan?.name || "Sin plan activo"

  const [loading, setLoading] = useState(true)
  const [snapshot, setSnapshot] = useState({
    clinicsCount: 0,
    chairsCount: 0,
    doctorsCount: 0,
    usersCount: 0,
    treatmentsCount: 0,
    mappingsCount: 0,
    scheduleSavedAt: "",
    scheduleEnabledDays: 0,
    blockedDatesCount: 0,
  })

  useEffect(() => {
    let cancelled = false

    async function loadSnapshot() {
      try {
        setLoading(true)

        const clinics = await requestFirstSuccess(
          ["/api/multi-clinic/user-clinics"],
          { userId }
        )

        const clinicsCount = Array.isArray(clinics) ? clinics.length : 0

        let chairsCount = 0
        let doctorsCount = 0
        let usersCount = 0
        let treatmentsCount = 0

        const mappingsCount = 0

        if (clinicId && clinicId !== "ALL") {
          const [chairs, doctors, users, treatments] = await Promise.all([
            requestFirstSuccess(["/api/chairs"], { clinicId }),
            requestFirstSuccess(["/api/multi-clinic/clinic-doctors"], { clinicId }),
            requestFirstSuccess(["/api/multi-clinic/clinic-users"], { clinicId }),
            requestFirstSuccess(["/api/treatments"], { clinicId, limit: 300 }),
          ])

          chairsCount = Array.isArray(chairs) ? chairs.length : 0
          doctorsCount = Array.isArray(doctors) ? doctors.length : 0
          usersCount = Array.isArray(users) ? users.length : 0
          treatmentsCount = Array.isArray(treatments) ? treatments.length : 0
        }

        const storedSchedule = parseStoredConfig(
          localStorage.getItem(getScheduleStorageKey(clinicId || "default"))
        )

        const scheduleSavedAt = storedSchedule?.meta?.savedAt || ""
        const scheduleEnabledDays = countEnabledDays(storedSchedule?.hours)
        const blockedDatesCount = countBlockedDates(
          storedSchedule?.breaks?.blockedDatesText
        )

        if (!cancelled) {
          setSnapshot({
            clinicsCount,
            chairsCount,
            doctorsCount,
            usersCount,
            treatmentsCount,
            mappingsCount,
            scheduleSavedAt,
            scheduleEnabledDays,
            blockedDatesCount,
          })
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadSnapshot()

    return () => {
      cancelled = true
    }
  }, [clinicId, userId])

  const groups = useMemo(() => {
    const infrastructureStatus =
      snapshot.clinicsCount === 0
        ? {
            label: "Pendiente",
            tone: "warning",
            detail: "Aún no hay sedes registradas.",
          }
        : snapshot.chairsCount === 0
          ? {
              label: "Incompleto",
              tone: "warning",
              detail: "Ya hay sedes, pero faltan sillas o recursos físicos.",
            }
          : {
              label: "Listo",
              tone: "success",
              detail: "La base física de la clínica ya está configurada.",
            }

    const teamStatus =
      snapshot.doctorsCount === 0
        ? {
            label: "Pendiente",
            tone: "warning",
            detail: "Faltan doctores para que la operación clínica quede lista.",
          }
        : snapshot.usersCount === 0
          ? {
              label: "Incompleto",
              tone: "warning",
              detail: "Ya hay doctores, pero todavía faltan usuarios operativos.",
            }
          : {
              label: "Listo",
              tone: "success",
              detail: "Equipo clínico y usuarios base ya están cargados.",
            }

    const scheduleStatus =
      snapshot.scheduleSavedAt
        ? {
            label: "Guardado",
            tone: "success",
            detail: `${snapshot.scheduleEnabledDays} día(s) activos y ${snapshot.blockedDatesCount} bloqueo(s) cargados.`,
          }
        : {
            label: "Pendiente",
            tone: "warning",
            detail: "Todavía no se guarda una configuración base de agenda.",
          }

    const treatmentsStatus =
      snapshot.treatmentsCount > 0
        ? {
            label: "Listo",
            tone: "success",
            detail: `${snapshot.treatmentsCount} tratamiento(s) disponibles en la clínica activa.`,
          }
        : {
            label: "Pendiente",
            tone: "warning",
            detail: "Aún no hay tratamientos cargados en el catálogo.",
          }

    const engineStatus =
      snapshot.mappingsCount > 0
        ? {
            label: "En marcha",
            tone: "info",
            detail: `${snapshot.mappingsCount} mapeo(s) odontológicos detectados.`,
          }
        : {
            label: "Por afinar",
            tone: "warning",
            detail:
              "El motor clínico está disponible, pero el contador de mapeos aún no está conectado a backend.",
          }

    const systemStatus =
      currentPlanName && currentPlanName !== "Sin plan activo"
        ? {
            label: "Activo",
            tone: "info",
            detail: `Plan actual: ${currentPlanName}.`,
          }
        : {
            label: "Revisar",
            tone: "warning",
            detail: "Conviene revisar plan, suscripción y configuración general.",
          }

    return [
      {
        title: "Operación de la clínica",
        subtitle: "Define estructura, equipo y disponibilidad antes del trabajo diario.",
        items: [
          {
            title: "Sedes y recursos",
            description:
              "Administra sedes, consultorios, sillas y capacidad operativa base de la clínica.",
            to: "/settings/clinic-resources",
            badge: "Clínicas + sillas",
            icon: "🏥",
            accent: "#dbeafe",
            accentBorder: "#bfdbfe",
            metric: `${snapshot.clinicsCount} sede(s) · ${snapshot.chairsCount} silla(s)`,
            status: infrastructureStatus,
          },
          {
            title: "Equipo y accesos",
            description:
              "Gestiona doctores, usuarios, perfiles operativos y control de acceso del sistema.",
            to: "/settings/team-access",
            badge: "Doctores + usuarios",
            icon: "👥",
            accent: "#dcfce7",
            accentBorder: "#bbf7d0",
            metric: `${snapshot.doctorsCount} doctor(es) · ${snapshot.usersCount} usuario(s)`,
            status: teamStatus,
          },
          {
            title: "Agenda y disponibilidad",
            description:
              "Centraliza horarios, descansos, bloqueos y reglas base de disponibilidad clínica.",
            to: "/settings/schedule-availability",
            badge: "Nuevo centro operativo",
            icon: "🗓️",
            accent: "#fef3c7",
            accentBorder: "#fde68a",
            metric: snapshot.scheduleSavedAt
              ? `Guardado ${new Date(snapshot.scheduleSavedAt).toLocaleDateString("es-MX")}`
              : "Sin guardar todavía",
            status: scheduleStatus,
          },
        ],
      },
      {
        title: "Motor del sistema",
        subtitle: "Configura tratamientos, permisos y lógica clínica avanzada.",
        items: [
          {
            title: "Tratamientos y permisos",
            description:
              "Concentra catálogo, duración, precios base y permisos clínicos por tratamiento.",
            to: "/settings/treatments-permissions",
            badge: "Catálogo + permisos",
            icon: "🧾",
            accent: "#e0f2fe",
            accentBorder: "#bae6fd",
            metric: `${snapshot.treatmentsCount} tratamiento(s)`,
            status: treatmentsStatus,
          },
          {
            title: "Motor clínico",
            description:
              "Agrupa reglas clínicas, matriz operativa y mapeo odontológico en un solo frente.",
            to: "/settings/clinical-engine",
            badge: "Reglas + matriz + mapeo",
            icon: "🧠",
            accent: "#fee2e2",
            accentBorder: "#fecaca",
            metric: "Conexión pendiente",
            status: engineStatus,
          },
        ],
      },
      {
        title: "Sistema y cuenta",
        subtitle: "Mantén control global del sistema, plan y administración de cuenta.",
        items: [
          {
            title: "Sistema y cuenta",
            description:
              "Consulta plan, suscripción, configuración general y administración global.",
            to: "/settings/system-account",
            badge: "General + plan + suscripción",
            icon: "⚙️",
            accent: "#ede9fe",
            accentBorder: "#ddd6fe",
            metric: currentPlanName,
            status: systemStatus,
          },
        ],
      },
    ]
  }, [snapshot, currentPlanName])

  return (
    <div style={ui.page}>
      <div
        style={{
          borderRadius: 24,
          padding: 22,
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e293b 52%, #2563eb 100%)",
          color: "#ffffff",
          marginBottom: 18,
          boxShadow: "0 22px 50px rgba(15,23,42,0.20)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.25fr 1fr",
            gap: 16,
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.14)",
                fontSize: 11,
                fontWeight: 800,
                marginBottom: 10,
              }}
            >
              CONFIGURACIÓN CENTRALIZADA
            </div>

            <div
              style={{
                fontSize: 30,
                fontWeight: 900,
                lineHeight: 1.05,
              }}
            >
              Centro de configuración
            </div>

            <div
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.82)",
                marginTop: 8,
                maxWidth: 760,
                lineHeight: 1.45,
              }}
            >
              Define cómo opera la clínica, quién trabaja en ella y cómo se gobierna el motor clínico, sin dispersar la administración en pantallas huérfanas.
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 10,
            }}
          >
            <HeroMetricCard label="Grupos" value="3" />
            <HeroMetricCard label="Módulos" value="6" />
            <HeroMetricCard
              label="Sede activa"
              value={clinicId && clinicId !== "ALL" ? "Definida" : "Pendiente"}
            />
            <HeroMetricCard label="Estado" value={loading ? "Cargando..." : "Actualizado"} />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 18 }}>
        {groups.map((group) => (
          <div
            key={group.title}
            style={{
              ...ui.card,
              padding: 18,
            }}
          >
            <div style={{ marginBottom: 14 }}>
              <div style={ui.sectionTitle}>{group.title}</div>
              <div style={{ ...ui.sectionSubtitle, marginTop: 4 }}>
                {group.subtitle}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              {group.items.map((item) => (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  style={{
                    textAlign: "left",
                    padding: 16,
                    borderRadius: 18,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)"
                    e.currentTarget.style.boxShadow = "0 16px 28px rgba(15,23,42,0.08)"
                    e.currentTarget.style.borderColor = "#bfdbfe"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)"
                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(15,23,42,0.04)"
                    e.currentTarget.style.borderColor = "#e2e8f0"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: 14,
                        display: "grid",
                        placeItems: "center",
                        background: item.accent,
                        border: `1px solid ${item.accentBorder}`,
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            padding: "4px 8px",
                            borderRadius: 999,
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            color: "#64748b",
                            fontSize: 11,
                            fontWeight: 800,
                          }}
                        >
                          {item.badge}
                        </div>

                        <StatusPill
                          label={loading ? "Cargando..." : item.status.label}
                          tone={loading ? "info" : buildStatusTone(item.status.tone)}
                        />
                      </div>

                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 900,
                          color: "#0f172a",
                          marginBottom: 6,
                          lineHeight: 1.2,
                        }}
                      >
                        {item.title}
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          color: "#64748b",
                          lineHeight: 1.45,
                        }}
                      >
                        {item.description}
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          padding: "10px 12px",
                          borderRadius: 12,
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "#64748b",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: 0.35,
                            marginBottom: 4,
                          }}
                        >
                          Estado actual
                        </div>

                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 900,
                            color: "#0f172a",
                            marginBottom: 4,
                          }}
                        >
                          {loading ? "Consultando..." : item.metric}
                        </div>

                        <div
                          style={{
                            fontSize: 12.5,
                            color: "#64748b",
                            lineHeight: 1.4,
                          }}
                        >
                          {loading
                            ? "Estamos leyendo la configuración actual."
                            : item.status.detail}
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          fontSize: 12,
                          fontWeight: 800,
                          color: "#2563eb",
                        }}
                      >
                        Abrir módulo →
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function HeroMetricCard({ label, value }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.16)",
      }}
    >
      <div style={{ fontSize: 11, opacity: 0.76, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900 }}>{value}</div>
    </div>
  )
}

function StatusPill({ label, tone = "info" }) {
  const palette = {
    success: {
      background: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
    },
    warning: {
      background: "#fffbeb",
      border: "#fde68a",
      color: "#92400e",
    },
    danger: {
      background: "#fef2f2",
      border: "#fecaca",
      color: "#991b1b",
    },
    info: {
      background: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
  }

  const visual = palette[tone] || palette.info

  return (
    <div
      style={{
        display: "inline-flex",
        padding: "4px 8px",
        borderRadius: 999,
        background: visual.background,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 11,
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
  )
}